/**
 * Bed Category catalog constants (B2 / S1–S3).
 *
 * Single source for the IPD tariff-form option sets + the client-side GST
 * smart-default mirror. Mirrors the backend `app/core/ipd_tax.py` — the
 * server default is authoritative; this mirror exists ONLY for the live
 * as-you-type suggestion on BedCatalogPage. Both sides carry the SAME
 * boundary table (exempt care level → 0; > ₹5000/day → 5; exactly ₹5000 → 0)
 * and are tested identically (contract-reconcile asserts they agree).
 */

export const BILLING_UNITS = [
  { value: 'PER_DAY', label: 'Per day' },
  { value: 'PER_SESSION', label: 'Per session' },
  { value: 'PER_HOUR', label: 'Per hour' },
];

/** Charge-field label suffix per billing unit (S3 — labels follow the unit). */
export const BILLING_UNIT_SUFFIX = {
  PER_DAY: 'day',
  PER_SESSION: 'session',
  PER_HOUR: 'hour',
};

/**
 * The three tariff inputs are the SAME fields under every billing unit —
 * only their unit label flips (PER_DAY default).
 */
export function chargeLabels(billingUnit) {
  const unit = BILLING_UNIT_SUFFIX[billingUnit] || BILLING_UNIT_SUFFIX.PER_DAY;
  return {
    per_day_charge: `Room rent / ${unit}`,
    nursing_charge_per_day: `Nursing / ${unit}`,
    rmo_charge_per_day: `RMO / ${unit}`,
  };
}

export const CARE_LEVELS = ['GENERAL', 'HDU', 'ICU', 'NICU', 'PICU', 'ISOLATION', 'DAYCARE'];

export const TIERS = ['STANDARD', 'SEMI', 'PREMIUM', 'DELUXE', 'SUITE'];

/** Shared GST select set — constrained in-UI, mirrors ALLOWED_GST_RATES. */
export const GST_RATES = [0, 5, 12, 18, 28];

/**
 * ICU-class care levels are GST-EXEMPT room rent (CBIC 03/2022) — every
 * ICU-class value in the care_level enum. Mirrors GST_EXEMPT_CARE_LEVELS in
 * app/core/ipd_tax.py — ONE list per side, never re-declared elsewhere.
 */
export const GST_EXEMPT_CARE_LEVELS = ['ICU', 'NICU', 'PICU'];

/**
 * Client mirror of the server GST smart-default (app/core/ipd_tax.py
 * `suggest_gst_rate`): exempt care level → 0; else per-day charge STRICTLY
 * above ₹5000 → 5 (no-ITC room-rent rule); else 0 (exactly ₹5000 → 0).
 * Suggestion only — the admin can always override; D1 owns real GST billing.
 */
export function suggestGstRate(careLevel, perDayCharge) {
  if (careLevel && GST_EXEMPT_CARE_LEVELS.includes(String(careLevel).toUpperCase())) {
    return 0;
  }
  const charge = Number(perDayCharge);
  if (Number.isFinite(charge) && charge > 5000) return 5;
  return 0;
}
